import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip, Row, Select } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import fileDownload from 'js-file-download';

import styles from '../styles';
import { errorNotification, successNotification } from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';
import { clearStoreOutwards, getStoreOutwards } from '../Actions';
import { currentFinancialYear, financialYears } from '../financial-years';

const StoreOutwardList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const storeOutwards = useSelector(state => state.storeOutwards);

  const [Year, SetYear] = useState(currentFinancialYear);

  useEffect(() => {
    dispatch(getStoreOutwards({ year: Year }));

    return () => {
      dispatch(clearStoreOutwards());
    };
  }, [Year]);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/store-outwards/${id}`);
      successNotification('Store outward deleted');
      dispatch(getStoreOutwards());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateStoreOutward) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/storeoutward/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteStoreOutward) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const onFileDownload = useCallback(async id => {
    try {
      const { data } = await Api.getFile(`/store-outwards/download/${id}`);
      fileDownload(data, `store-outwards-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const downloadColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="Download">
          <DownloadOutlined
            className={classes.tableIcon}
            onClick={() => onFileDownload(record.id)}
          />
        </Tooltip>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Outward #',
        dataIndex: 'outwardNo',
        sorter: sorter('outwardNo'),
        onFilter: onFilter('outwardNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Outward Date',
        dataIndex: 'outwardDate',
        sorter: dateSorter('outwardDate'),
        onFilter: onDateFilter('outwardDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: outwardDate =>
          outwardDate ? moment(outwardDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Products',
        dataIndex: 'products',
        sorter: sorter('products'),
        onFilter: onFilter('products'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Fabrics',
        dataIndex: 'fabrics',
        sorter: sorter('fabrics'),
        onFilter: onFilter('fabrics'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Styles',
        dataIndex: 'styles',
        sorter: sorter('styles'),
        onFilter: onFilter('styles'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Items',
        dataIndex: 'items',
        sorter: sorter('items'),
        onFilter: onFilter('items'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Accessories',
        dataIndex: 'accessories',
        sorter: sorter('accessories'),
        onFilter: onFilter('accessories'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Alter/Reprocess',
        dataIndex: 'isAlter',
        sorter: sorter('isAlter'),
        onFilter: onFilter('isAlter'),
        filterDropdown,
        filterIcon,
        render: isAlter => (isAlter ? 'Yes' : 'No'),
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.updateStoreOutward)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteStoreOutward)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Download',
        render: downloadColumn,
        fixed: 'right',
        width: '3%',
      }
    );

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 3000, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Store Outward"
        moduleLink="/storeoutward/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Row>
          <Select
            value={Year}
            style={{ width: 150 }}
            onChange={value => SetYear(value)}
          >
            {financialYears.map(el => (
              <Select.Option key={el} value={el}>
                {el}
              </Select.Option>
            ))}
          </Select>
        </Row>
        <br />

        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={storeOutwards}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

StoreOutwardList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(StoreOutwardList);
